import React, { ChangeEventHandler, FocusEventHandler } from 'react';

/**
 * This is the base for the website before you are logged in, and it
 * renders at '/'. If you hit this page and are already logged in, you are
 * forwarded to '/home', otherwise you get a login form.
 */

import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';

import * as Yup from 'yup';
import { Formik, FormikConfig } from 'formik';
import { fetchAuthenticationToken } from '~/services/account';
import { login } from '~/components/Bouncer/Bouncer';
import { Spinner } from 'react-bootstrap';

type FormikSubmit = FormikConfig<{ email: string; password: string }>['onSubmit'];

interface InputGroupProps {
  name: string;
  label: string;
  handleChange: ChangeEventHandler;
  handleBlur: FocusEventHandler;
  values: Record<string, any>;
  errors: Record<string, any>;
  disabled?: boolean;
  type?: string;
  autocomplete?: string;
  submitForm: () => void;
}

export function InputGroup( {
  name,
  label,
  handleChange,
  handleBlur,
  values,
  errors,
  disabled,
  type = 'text',
  autocomplete,
  submitForm,
}: InputGroupProps ) {
  const onKeyUp = ( e: React.KeyboardEvent<HTMLInputElement> ) => {
    if ( e.key === 'Enter' ) {
      submitForm();
    }
  };

  return (
    <Form.Group controlId={ `${name}-field` } className={ disabled ? 'disabled' : '' }>
      <Form.Label>{ label }</Form.Label>
      <Form.Control
        type={ type }
        name={ name }
        onChange={ handleChange }
        onBlur={ handleBlur }
        value={ values[name] }
        isInvalid={ errors[name] }
        disabled={ disabled }
        autoComplete={ autocomplete }
        onKeyUp={ onKeyUp }
      />
      { errors[name] && <Form.Control.Feedback type="invalid">{ errors[name] }</Form.Control.Feedback> }
    </Form.Group>
  );
}

export function Login() {
  const [ loading, setLoading ] = React.useState( false );

  const _handleSubmit: FormikSubmit = async ( values, actions ) => {
    setLoading( true );

    const response = await fetchAuthenticationToken( values.email, values.password, 'session:create:ops-portal' );

    if ( response.success ) {
      login( response.token, '/home' );

      return;
    }

    setLoading( false );

    if ( !response.success ) {
      for ( const error of response.errors ) {
        if ( !error.key ) {
          actions.setFieldError( 'email', error.message );
        } else {
          actions.setFieldError( error.key, error.message );
        }
      }
    } else {
      actions.setFieldError( 'email', 'Unable to reach server.' );
    }
  };

  return (
    <div className="justify-content-center d-flex">
      <div id="login">
        <Card>
          <Card.Body>
            <Formik
              initialValues={ {
                email: '',
                password: '',
              } }
              validationSchema={ Yup.object( {
                email: Yup.string().required( 'This field is required' ),
                password: Yup.string().required( 'This field is required' ),
              } ) }
              onSubmit={ _handleSubmit }
              validateOnChange={ false }
              validateOnBlur={ false }
            >
              { ( { handleSubmit, handleChange, handleBlur, submitForm, values, errors } ) => (
                <Form onSubmit={ ( e ) => e.preventDefault() }>
                  <Row>
                    <Col>
                      <InputGroup
                        name="email"
                        label="Email address"
                        autocomplete="username"
                        handleChange={ handleChange }
                        handleBlur={ handleBlur }
                        values={ values }
                        errors={ errors }
                        submitForm={ submitForm }
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <InputGroup
                        name="password"
                        label="Password"
                        type="password"
                        autocomplete="current-password"
                        handleChange={ handleChange }
                        handleBlur={ handleBlur }
                        values={ values }
                        errors={ errors }
                        submitForm={ submitForm }
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col className="text-right">
                      <Button type="submit" variant="primary" disabled={ loading } onClick={ () => handleSubmit() }>
                        { loading ? <Spinner animation="border" /> : <>Continue</> }
                      </Button>
                    </Col>
                  </Row>
                </Form>
              ) }
            </Formik>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
}
